import { cn } from '@/lib';
import {
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode
} from 'react';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { Checkbox } from './basics/Checkbox';


export interface DropdownProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const Dropdown = DropdownMenu.Root

export interface DropdownTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
}

export function DropdownTriggerButton({ children, className, onClick, ...props }: DropdownTriggerProps) {
  return (
    <DropdownMenu.Trigger asChild disabled={props.disabled}>
      <button
        type="button"
        onClick={(e) => {
          onClick?.(e);
        }}
        aria-haspopup="menu"
        className={cn(
          'py-2 px-2.5 inline-flex items-center gap-x-1.5 text-xs font-medium rounded-lg border border-border bg-surface text-onsurface shadow-2xs hover:bg-surface-darker disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:bg-surface-darker',
          className
        )}
        {...props}
      >
        {children}
      </button>
    </DropdownMenu.Trigger >
  );
}

export function DropdownTrigger({ children, className, onClick, ...props }: DropdownTriggerProps) {
  return (
    <DropdownMenu.Trigger asChild disabled={props.disabled}>
      {children}
    </DropdownMenu.Trigger>
  )
}

export interface DropdownContentProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  width?: 'auto' | 'trigger' | number;
  className?: string;
}

export const DropdownContent = ({ children, width = 'auto', className }: { children: ReactNode, width?: 'auto' | 'trigger' | number, className?: string }) => (
  <DropdownMenu.Portal>
    <DropdownMenu.Content className={cn("bg-surface shadow-md rounded-lg z-500 min-w-40 border border-border/50", className)}
      avoidCollisions={true}
      collisionPadding={10}
      style={{
        width,
        maxHeight: "var(--radix-dropdown-menu-content-available-height)",
        overflowY: "auto",
      }}>
      <ScrollArea.Root style={{ maxHeight: 400 }}>
        <ScrollArea.Viewport>
          {children}
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar orientation="vertical" />
      </ScrollArea.Root>
    </DropdownMenu.Content>
  </DropdownMenu.Portal>
);

export interface DropdownItemProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  disabled?: boolean;
  onSelect: () => void;
}

export function DropdownItem({ children, className, disabled, onSelect, ...props }: DropdownItemProps) {

  return (
    <DropdownMenu.Item onSelect={onSelect} asChild className="focus:outline-none">
      <div
        role="menuitem"
        tabIndex={disabled ? -1 : 0}
        className={cn(
          'flex items-center gap-x-3 py-1.5 px-2 cursor-pointer rounded-lg text-[13px] text-onsurface',
          !disabled && 'hover:bg-surface-darker/60 focus:bg-surface-darker',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
        {...props}
      >
        {children}
      </div></DropdownMenu.Item>
  );
}

export interface DropdownCheckboxItemProps extends Omit<HTMLAttributes<HTMLLabelElement>, 'onChange'> {
  children: ReactNode;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
}

export function DropdownCheckboxItem({
  children,
  className,
  checked,
  onChange,
  disabled,
  ...props
}: DropdownCheckboxItemProps) {
  return (
    <label
      className={cn(
        'flex items-center gap-x-3 py-1.5 px-2 cursor-pointer rounded-lg',
        !disabled && 'hover:bg-surface-darker',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      {...props}
    >
      <Checkbox 
        checked={checked || false}
        onChange={() => onChange?.(!checked)}
        ariaLabel=''
      />
      <span className="text-[13px] text-onsurface">{children}</span>
    </label>
  );
}

export interface DropdownEmptyProps {
  children: ReactNode;
}

export function DropdownEmpty({ children }: DropdownEmptyProps) {
  return (
    <div className="py-2 px-3 text-sm text-muted-foreground">{children}</div>
  );
}

export interface DropdownLoadingProps {
  rows?: number;
}

export function DropdownLoading({ rows = 3 }: DropdownLoadingProps) {
  return (
    <div className="py-2 px-3 animate-pulse space-y-2">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="h-4 bg-surface-darker rounded" />
      ))}
    </div>
  );
}


export function DropDownSelectToClose({ children, doSelect }: { children: ReactNode, doSelect: () => void }) {
  return <DropdownItem onSelect={() => {
    doSelect();
  }}>{children}</DropdownItem>;
}

export function DropDownSelectableContent({ doSelect, value, isSelected }: { doSelect: () => void, value: string, isSelected: boolean }) {
  return <DropDownSelectToClose doSelect={doSelect}>
    <div
      key={value}
      onClick={() => {
        doSelect();
      }}
      className={`w-full flex items-center gap-x-3 py-1.5 px-2 rounded-lg text-[13px] font-normal hover:bg-surface-darker disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:bg-surface-darker ${isSelected
        ? 'text-primary'
        : 'text-onsurface'}`}
    >
      {value}
    </div>
  </DropDownSelectToClose>;
}